import { Text } from '@mantine/core';
import { forwardRef, useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react';
import {
	flowDateSlots,
	flowSignatureSlots,
} from '@dealstack/shared/lib/common/business/documentFlow';
import { signatureSlotKey } from '@dealstack/shared/lib/common/business/signatureSlotKey';
import type { DocumentFlowDoc } from '@dealstack/shared/lib/common/business/documentFlow';

// The bind panel's own heading, so the two data-point lists read as one thing in two places.
import {
	EyebrowLabel,
	SelectDataPointHeading,
} from '@dealstack/frontend/shared/components/FieldBinding/BindPanel/bindPanel.styled';

import { DateAnchorPalette } from './DateAnchorPalette';
import { FieldPalette } from './FieldPalette';
import { FlowDocumentEditor } from './FlowDocumentEditor';
import { SignatureSlotPalette } from './SignatureSlotPalette';
import type { SignatureSlotPaletteItem } from './SignatureSlotPalette';
import { EditorPaneSplit, EditorSidePanel, PaletteScroll } from './flowDocumentEditor.styled';
import type {
	FlowDocumentEditorHandle,
	FlowDocumentEditorPaneProps,
} from './flowDocumentEditor.types';

/**
 * The editor beside its palette.
 *
 * Composed here so the insert-at-caret command stays inside the editor: the palette says which
 * field or signature, the editor decides where, and nothing outside needs a handle on the
 * ProseMirror view.
 *
 * `mode` picks which palette sits beside it — data points, or the signatures this document has
 * to carry. The DOCUMENT is the same either way and is never remounted between the two: it
 * reads its content once, on mount, so swapping it out would drop the unsaved edit.
 */
export const FlowDocumentEditorPane = forwardRef<
	FlowDocumentEditorHandle,
	FlowDocumentEditorPaneProps
>(
	(
		{
			document: flowDocument,
			fields,
			knownFieldLabels,
			mode = 'fields',
			signatureSlotList = [],
			signatureSlots,
			showDateAnchors = false,
			showStampMetadata,
			onChange,
			onUnrepresentableContent,
			editable = true,
			paletteTabs,
			panelHeader,
			paletteHeader,
			rulesPanel,
			ruleLabelFor,
			ruleActions,
		},
		ref
	) => {
		const editorRef = useRef<FlowDocumentEditorHandle>(null);

		/**
		 * The document as the editor last reported it, or null for "unchanged since it opened".
		 *
		 * Held so the palette's placed/unplaced dots follow an anchor deleted with the keyboard,
		 * not only one removed from the palette itself. Null is not "no document" — it is the
		 * document this pane was given, which is exactly what the editor means by it.
		 */
		const [edited, setEdited] = useState<DocumentFlowDoc | null>(null);

		const handleChange = useCallback(
			(next: DocumentFlowDoc | null) => {
				setEdited(next);
				onChange?.(next);
			},
			[onChange]
		);

		// Read through the shared flow-model walker rather than off the ProseMirror view, so
		// this and the save agree on what "placed" means by construction.
		const placedKeys = useMemo(
			() =>
				new Set(
					flowSignatureSlots(edited ?? flowDocument).map((slot) => signatureSlotKey(slot))
				),
			[edited, flowDocument]
		);

		// Same walker as the save, so the list is numbered as `placementIndex` will be.
		const placedDateIds = useMemo(
			() => flowDateSlots(edited ?? flowDocument).map((slot) => slot.templateDatePlacementId),
			[edited, flowDocument]
		);

		useImperativeHandle(ref, () => ({
			setSelectionRule: (ruleId) => editorRef.current?.setSelectionRule(ruleId) ?? 0,
			clearRules: (ruleIds) => editorRef.current?.clearRules(ruleIds),
			refreshRules: () => editorRef.current?.refreshRules(),
			insertField: (field) => editorRef.current?.insertField(field),
			insertSignature: (slot) => editorRef.current?.insertSignature(slot) ?? false,
			insertSignatureBlock: () => editorRef.current?.insertSignatureBlock() ?? false,
			removeSignature: (slot) => editorRef.current?.removeSignature(slot),
			locateSignature: (slot) => editorRef.current?.locateSignature(slot),
			placedSignatureKeys: () =>
				editorRef.current?.placedSignatureKeys() ?? new Set<string>(),
			insertDate: (templateDatePlacementId) =>
				editorRef.current?.insertDate(templateDatePlacementId) ?? false,
			removeDate: (templateDatePlacementId) =>
				editorRef.current?.removeDate(templateDatePlacementId),
			locateDate: (templateDatePlacementId) =>
				editorRef.current?.locateDate(templateDatePlacementId),
			placedDateIds: () => editorRef.current?.placedDateIds() ?? [],
			markSaved: () => editorRef.current?.markSaved(),
		}));

		const paletteItems: SignatureSlotPaletteItem[] = signatureSlotList.map((slot) => ({
			...slot,
			isPlaced: placedKeys.has(signatureSlotKey(slot)),
		}));

		return (
			<EditorPaneSplit>
				<FlowDocumentEditor
					ref={editorRef}
					document={flowDocument}
					fields={fields}
					editable={editable}
					showStampMetadata={showStampMetadata ?? true}
					{...(knownFieldLabels === undefined ? {} : { knownFieldLabels })}
					{...(ruleLabelFor === undefined ? {} : { ruleLabelFor })}
					{...(ruleActions === undefined ? {} : { ruleActions })}
					{...(signatureSlots === undefined ? {} : { signatureSlots })}
					// Always wired: the pane tracks the edited document itself, for the palette's
					// placed/unplaced dots, and forwards to the caller's `onChange` if there is one.
					onChange={handleChange}
					{...(onUnrepresentableContent === undefined
						? {}
						: { onUnrepresentableContent })}
				/>
				<EditorSidePanel>
					{panelHeader}
					{/* Pinned. Everything else in the panel scrolls past it — switching surface
					    is how you leave, so it is the one control that has to stay in reach. */}
					{paletteTabs}
					<PaletteScroll data-testid="palette-scroll">
						{mode === 'rules' && (
							<>
								<SelectDataPointHeading>
									<EyebrowLabel>Rules</EyebrowLabel>
									<Text size="xs" c="dark.0">
										Select the content that should only appear sometimes, then
										press <b>If…</b> in the toolbar.
									</Text>
								</SelectDataPointHeading>
								{rulesPanel}
							</>
						)}
						{mode === 'signatures' && (
							<>
								<SelectDataPointHeading>
									<EyebrowLabel>Signatures</EyebrowLabel>
									<Text size="xs" c="dark.0">
										Place all of these, or none of them. Leave them unplaced and
										the stamps go on a signatures page appended to the document.
									</Text>
								</SelectDataPointHeading>
								<SignatureSlotPalette
									slots={paletteItems}
									onInsert={(slot) =>
										editorRef.current?.insertSignature(slot) ?? false
									}
									onInsertBlock={() =>
										editorRef.current?.insertSignatureBlock() ?? false
									}
									onLocate={(slot) => editorRef.current?.locateSignature(slot)}
									onRemove={(slot) => editorRef.current?.removeSignature(slot)}
								/>
								{showDateAnchors && (
									<>
										<SelectDataPointHeading>
											<EyebrowLabel>Date</EyebrowLabel>
											<Text size="xs" c="dark.0">
												Where the effective date is written once every
												signature has landed.
											</Text>
										</SelectDataPointHeading>
										<DateAnchorPalette
											placedIds={placedDateIds}
											onInsert={(id) =>
												editorRef.current?.insertDate(id) ?? false
											}
											onLocate={(id) => editorRef.current?.locateDate(id)}
											onRemove={(id) => editorRef.current?.removeDate(id)}
										/>
									</>
								)}
							</>
						)}
						{mode === 'fields' && (
							<>
								<SelectDataPointHeading>
									<EyebrowLabel>Data points</EyebrowLabel>
									{/* Not `dimmed`: v7 resolves that token to the light end of
									    the grey ramp, and this line was barely on the page. */}
									<Text size="xs" c="dark.0">
										Click one to insert it at the cursor, drag it to place it,
										or type <code>{'{{'}</code> in the document.
									</Text>
								</SelectDataPointHeading>
								{paletteHeader}
								<FieldPalette
									fields={fields}
									onInsert={(field) => editorRef.current?.insertField(field)}
								/>
							</>
						)}
					</PaletteScroll>
				</EditorSidePanel>
			</EditorPaneSplit>
		);
	}
);

FlowDocumentEditorPane.displayName = 'FlowDocumentEditorPane';
